import { useState, useEffect } from 'react';
import { Heart, Gift, Users, Briefcase, CheckCircle, ArrowRight, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Donate = () => {
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const donationAmounts = [25, 50, 100, 250, 500, 1000];

  const impactItems = [
    { amount: 25, impact: "Provides learning materials for one student for a month" },
    { amount: 50, impact: "Sponsors a student's participation in a workshop" },
    { amount: 100, impact: "Covers technology access for one student for a semester" },
    { amount: 250, impact: "Funds a complete course enrollment for one student" },
    { amount: 500, impact: "Provides mentorship and career coaching for one student" },
    { amount: 1000, impact: "Sponsors a student's full program participation" },
  ];

  const getDonationAmount = () => {
    if (customAmount && parseFloat(customAmount) > 0) {
      return parseFloat(customAmount);
    }
    return selectedAmount;
  };

  const handlePayPalDonation = async () => {
    const amount = getDonationAmount();
    if (amount < 1) {
      toast.error('Please select or enter a donation amount');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/paypal/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amount,
          payment_type: 'donation',
          description: `Donation of $${amount}`,
          donor_name: donorName || 'Anonymous',
          donor_email: donorEmail || null
        })
      });

      const data = await response.json();
      
      if (data.success && data.approval_url) {
        // Redirect to PayPal approval page
        window.location.href = data.approval_url;
      } else {
        // Fallback to direct PayPal.me link if API fails
        console.warn('PayPal API failed, using fallback link');
        window.open('https://www.paypal.com/ncp/payment/V62DBYBX2L8WL', '_blank');
        setLoading(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      // Fallback to direct PayPal.me link
      window.open('https://www.paypal.com/ncp/payment/V62DBYBX2L8WL', '_blank');
      setLoading(false);
    }
  };

  // Check for returning from PayPal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentId = urlParams.get('paymentId');
    const payerId = urlParams.get('PayerID');
    
    if (paymentId && payerId) {
      // Capture the payment
      const capturePayment = async () => {
        setLoading(true);
        try {
          const response = await fetch(`${API_URL}/api/paypal/capture-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_id: paymentId })
          });

          const data = await response.json();
          if (data.success) {
            setPaymentSuccess(true);
            toast.success('Thank you for your generous donation!');
            // Clear URL params
            window.history.replaceState({}, document.title, window.location.pathname);
          } else {
            toast.error(data.detail || 'Payment capture failed');
          }
        } catch (err) {
          console.error('Capture error:', err);
          toast.error('Payment processing failed. Please contact support.');
        } finally {
          setLoading(false);
        }
      };
      capturePayment();
    }
  }, []);

  if (paymentSuccess) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22]">
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="w-24 h-24 rounded-full bg-[#00FFE0]/20 flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-12 h-12 text-[#00FFE0]" />
          </div>
          <h1 className="text-4xl font-black text-white mb-4">Thank You!</h1>
          <p className="text-xl text-[#94A3B8] mb-8">
            Your donation has been received.
            You&apos;re helping build the next generation of tech talent!
          </p>
          <p className="text-[#94A3B8] mb-8">
            As a 501(c)(3) nonprofit, your donation may be tax-deductible.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 bg-[#FF6A00] text-white font-bold rounded-xl px-8 py-4 hover:shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all"
          >
            Return Home
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-16 sm:py-24 bg-[#060F22] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#FF6A00]/10 via-[#060F22] to-[#060F22]"></div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="w-20 h-20 rounded-full bg-[#FF6A00]/20 flex items-center justify-center mx-auto mb-6">
            <Heart className="w-10 h-10 text-[#FF6A00]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-tight mb-4">
            Support the Next Generation
          </h1>
          <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
            Your donation helps young people from underserved communities break into tech careers. 
            Every contribution makes a real difference.
          </p>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 bg-[#0A1F44]/30">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Your Impact</h2>
            <p className="text-[#94A3B8]">See how your donation helps our students succeed</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {impactItems.map((item) => (
              <div 
                key={item.amount}
                className={`bg-[#0A1F44]/50 border rounded-xl p-6 transition-all cursor-pointer ${
                  selectedAmount === item.amount && !customAmount
                    ? 'border-[#FF6A00] shadow-[0_0_20px_rgba(255,106,0,0.2)]'
                    : 'border-[#00AEEF]/20 hover:border-[#FF6A00]/50'
                }`}
                onClick={() => { setSelectedAmount(item.amount); setCustomAmount(''); }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#FF6A00]/20 flex items-center justify-center">
                    <span className="text-[#FF6A00] font-bold">${item.amount}</span>
                  </div>
                  <Gift className="w-5 h-5 text-[#00FFE0]" />
                </div>
                <p className="text-white">{item.impact}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-16 bg-[#060F22]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-2xl p-8 sm:p-12">
            <h2 className="text-2xl font-bold text-white text-center mb-8">Make Your Donation</h2>
            
            {/* Amount Selection */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
              {donationAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => { setSelectedAmount(amount); setCustomAmount(''); }}
                  data-testid={`donate-amount-${amount}`}
                  className={`py-3 px-4 rounded-xl font-bold transition-all ${
                    selectedAmount === amount && !customAmount
                      ? 'bg-[#FF6A00] text-white shadow-[0_0_20px_rgba(255,106,0,0.3)]'
                      : 'bg-[#060F22] text-[#94A3B8] border border-[#00AEEF]/30 hover:border-[#FF6A00]/50 hover:text-white'
                  }`}
                >
                  ${amount}
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="mb-6">
              <label className="block text-sm text-[#94A3B8] mb-2">Or enter a custom amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]">$</span>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                  placeholder="Enter amount"
                  data-testid="donate-custom-amount"
                  className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl pl-8 pr-4 py-3 focus:border-[#FF6A00] focus:outline-none"
                />
              </div>
            </div>

            {/* Donor Info (Optional) */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">Name (optional)</label>
                <input
                  type="text"
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  placeholder="Your name"
                  data-testid="donate-name"
                  className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-3 focus:border-[#FF6A00] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">Email (for receipt)</label>
                <input
                  type="email"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="donate-email"
                  className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-3 focus:border-[#FF6A00] focus:outline-none"
                />
              </div>
            </div>

            {/* Selected Amount Display */}
            <div className="text-center mb-6">
              <p className="text-[#94A3B8]">You&apos;re donating</p>
              <p className="text-4xl font-black text-[#FF6A00]">${getDonationAmount()}</p>
            </div>

            {/* PayPal Button */}
            <div className="max-w-md mx-auto">
              <button
                onClick={handlePayPalDonation}
                disabled={loading}
                data-testid="paypal-donate-btn"
                className="w-full flex items-center justify-center gap-3 bg-[#0070BA] text-white font-bold rounded-xl py-4 hover:bg-[#005ea6] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.641.641 0 0 1 .633-.54h6.558c2.188 0 3.768.588 4.694 1.748.87 1.091 1.136 2.542.793 4.31-.023.115-.048.228-.076.34-.532 2.188-1.534 3.878-2.98 5.024-1.389 1.102-3.155 1.66-5.252 1.66H7.516a.641.641 0 0 0-.633.54l-.807 4.534z"/>
                      <path d="M18.138 7.614c-.027.137-.056.273-.089.408-.662 2.734-2.067 4.64-4.177 5.668-1.059.515-2.267.773-3.594.773h-.893a.641.641 0 0 0-.633.54l-.806 4.534-.23 1.293a.641.641 0 0 0 .633.74h3.96a.641.641 0 0 0 .633-.54l.026-.135.5-3.175.032-.174a.641.641 0 0 1 .633-.54h.399c2.58 0 4.6-.85 5.595-3.31.414-1.025.563-1.875.494-2.543-.043-.415-.158-.783-.484-1.539z"/>
                    </svg>
                    Donate with PayPal
                    <ExternalLink className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>

            <p className="text-center text-[#94A3B8] text-sm mt-6">
              NextGen Tech Pathways is a 501(c)(3) nonprofit organization. Your donation may be tax-deductible.
            </p>
          </div>
        </div>
      </section>

      {/* Student Work CTA */}
      <section className="py-16 bg-[#0A1F44]/30">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="bg-gradient-to-r from-[#FF6A00]/20 to-[#FFD700]/10 border border-[#FF6A00]/30 rounded-2xl p-8 sm:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-full bg-[#FF6A00]/30 flex items-center justify-center">
                  <Briefcase className="w-10 h-10 text-[#FF6A00]" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold text-white mb-2">Need a Website? Let Our Students Help!</h3>
                <p className="text-[#94A3B8] mb-4">
                  Post your project on our board and give students real-world experience while getting your website built. 
                  <span className="text-[#FFD700]"> Donors get priority placement!</span>
                </p>
                <a
                  href="/projects"
                  data-testid="view-projects-link"
                  className="inline-flex items-center gap-2 bg-[#FF6A00] text-white font-bold rounded-full px-6 py-3 hover:shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all"
                >
                  View Project Board
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Donate */}
      <section className="py-16 bg-[#060F22]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Why Support Us?</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#00AEEF]/20 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-[#00AEEF]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Empower Youth</h3>
              <p className="text-[#94A3B8]">Help young people from underserved communities gain the skills they need for successful tech careers.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#00FFE0]/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#00FFE0]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">100% Goes to Students</h3>
              <p className="text-[#94A3B8]">We&apos;re volunteer-run, so every dollar directly supports student programs, materials, and resources.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#FF6A00]/20 flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8 text-[#FF6A00]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Tax Deductible</h3>
              <p className="text-[#94A3B8]">As a registered 501(c)(3), your donation may be tax-deductible. We&apos;ll provide a receipt.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
